import { getDateFormat } from 'helpers/dates'
import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { injectIntl } from 'react-intl'
import moment from 'moment'
import { FiberManualRecord } from '@material-ui/icons'
import cn from 'classnames'
import {
  deleteJobInputItem,
  jobInputsClearStore,
  fetchJobInputPositionUpdate,
} from 'reducers/jobInputsAdmin/reducer'
import { deleteJobInputGroupItem } from 'reducers/jobInputGroupsAdmin/reducer'
import { fetchJobInputItemData } from 'reducers/jobInputItemsAdmin/reducer'
import { JOBS_WORKFLOW_ADMIN, JOB_WORKFLOW_TASKS } from 'constants/appRouting'
import * as ROUTE from 'constants/appRouting'
import Column from 'types/table/Column'
import Action from 'types/table/Action'
import ListFromTable from 'components/common/ListFromTable'
import ColumnCollection from 'types/table/ColumnCollection'
import ActionCollection from 'types/table/ActionCollection'
import UrlHelper from 'helpers/url'
import JobsWorkflowAdminPositionChange from '../JobsWorkflowAdminPositionChange'
import messages from '../translate'

class JobInputItemList extends ListFromTable {
  constructor(props) {
    super(props)
    this.asyncData = true
    this.loadingData = true
    this.getTableData = this.getInputsTableData
    this.clearReducerState = props.jobInputsClearStore

    this.state = {
      isDenseRows: false,
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    const { backUrlParams } = this.state
    const {
      loader,
      location: { search },
    } = nextProps
    const queryParams = new URLSearchParams(search)
    const backParams = queryParams.get('backUrlParams')

    this.loadingData = loader

    if (search && search.length && backParams && !backUrlParams) {
      this.setState({
        backUrlParams: backParams,
      })
    }

    return super.shouldComponentUpdate(nextProps, nextState)
  }

  getTableProps() {
    const {
      intl: { formatMessage },
      history: { push },
      location: { search },
    } = this.props
    const { isDenseRows, backUrlParams } = this.state
    let backUrl = JOBS_WORKFLOW_ADMIN
    let title

    if (search && search.length) {
      const queryParams = new URLSearchParams(search)

      title = `${queryParams.get('jobTitle')} | ${queryParams.get('taskTitle')}`
      backUrl = JOB_WORKFLOW_TASKS
    } else {
      title = formatMessage(messages.jobInputsList)
    }

    if (backUrlParams && backUrlParams.length) {
      backUrl = `${JOB_WORKFLOW_TASKS}${backUrlParams}`
    }

    return {
      goBack: () => {
        push(backUrl)
      },
      title,
      className: 'job-inputs-table',
      showFilterBar: true,
      sorting: true,
      paging: true,
      searching: true,
      creating: true,
      dense: {
        enabled: true,
        state: isDenseRows,
        handler: () => this.handleChangeDense(),
      },
    }
  }

  getConfirmDialogProps() {
    const {
      intl: { formatMessage },
    } = this.props
    const { confirmParams: { entityType } = {} } = this.state
    const isInput = entityType === 'input'

    return {
      title: isInput
        ? formatMessage(messages.deleteJobInput)
        : formatMessage(messages.deleteJobInputGroup),
      message: isInput
        ? formatMessage(messages.deleteJobInputMessage)
        : formatMessage(messages.deleteJobInputGroupMessage),
    }
  }

  getCreateButton() {
    const {
      intl: { formatMessage },
      location: { search },
    } = this.props
    let searchParams

    if (search && search.length) {
      const incomingParams = new URLSearchParams(search)
      const newParams = new URLSearchParams()

      newParams.append('taskTitle', incomingParams.get('taskTitle'))
      newParams.append('jobTitle', incomingParams.get('jobTitle'))
      searchParams = newParams.toString()
    }

    return [
      {
        title: formatMessage(messages.createJobInput),
        link: ROUTE.JOB_WORKFLOW_INPUTS_CREATE,
        params: searchParams,
      },
      {
        title: formatMessage(messages.createJobInputGroup),
        link: ROUTE.JOB_WORKFLOW_INPUT_GROUPS_CREATE,
        params: searchParams,
      },
    ]
  }

  getColumns() {
    const {
      intl: { formatMessage },
      fetchJobInputPositionUpdate,
      licensee: { usaDateFormat },
    } = this.props
    const columns = new ColumnCollection()
    const dateDisplay = getDateFormat(usaDateFormat)

    columns.add(
      new Column('id', 'ID', {
        sorting: true,
        render: ({ wrapped_input_id, wrapped_input_group_id }) =>
          wrapped_input_id || wrapped_input_group_id,
      }),
    )
    columns.add(
      new Column('position', formatMessage(messages.position), {
        sorting: true,
        render: (
          { position, wrapped_input_id, wrapped_input_group_id },
          rowIndex,
        ) => {
          const handlePositionChange = position => {
            fetchJobInputPositionUpdate({
              params: {
                type: wrapped_input_id
                  ? 'job-workflow-inputs'
                  : 'job-workflow-input-groups',
                id: wrapped_input_id || wrapped_input_group_id,
              },
              data: {
                position,
              },
              index: rowIndex,
            })
          }

          return (
            <JobsWorkflowAdminPositionChange
              position={position}
              onChange={handlePositionChange}
            />
          )
        },
      }),
    )
    columns.add(
      new Column('title', formatMessage(messages.title), {
        sorting: true,
        render: ({ wrapped_input_group_id, title }) =>
          `
            ${
              wrapped_input_group_id
                ? `[${formatMessage(messages.group).toUpperCase()}] `
                : ''
            }${title}
          `,
      }),
    )
    columns.add(
      new Column('workflow_title', formatMessage(messages.workflowTitle), {
        sorting: true,
      }),
    )
    columns.add(
      new Column(
        'workflow_task_title',
        formatMessage(messages.workflowTaskTitle),
        {
          sorting: true,
        },
      ),
    )
    columns.add(
      new Column('type', formatMessage(messages.inputType), {
        sorting: true,
      }),
    )
    columns.add(
      new Column('date_updated', formatMessage(messages.dateUpdated), {
        sorting: true,
        render: ({ date_updated }) => moment(date_updated).format(dateDisplay),
      }),
    )
    columns.add(
      new Column('enabled', formatMessage(messages.active), {
        sorting: true,
        render: ({ enabled }) => (
          <FiberManualRecord
            className={cn('user-status-icon', { 'status-enabled': enabled })}
          />
        ),
      }),
    )

    return columns
  }

  getActions() {
    const {
      intl: { formatMessage },
      history,
      location: { search },
      deleteJobInputItem,
      deleteJobInputGroupItem,
    } = this.props
    const actions = new ActionCollection()

    actions.add(
      new Action(
        'edit',
        formatMessage(messages.editJobInput),
        ({ wrapped_input_group_id, wrapped_input_id }) => {
          if (wrapped_input_id) {
            history.push(
              UrlHelper.create(ROUTE.JOB_WORKFLOW_INPUTS_EDIT, {
                id: wrapped_input_id,
              }),
            )
          } else {
            history.push(
              UrlHelper.create(ROUTE.JOB_WORKFLOW_INPUT_GROUPS_EDIT, {
                id: wrapped_input_group_id,
              }),
            )
          }
        },
      ),
    )
    actions.add(
      new Action(
        'delete',
        formatMessage(messages.deleteJobInput),
        ({ wrapped_input_group_id, wrapped_input_id }) => {
          this.onOpenConfirmDialog(
            {
              entityId: wrapped_input_group_id || wrapped_input_id,
              entityType: wrapped_input_id ? 'input' : 'inputGroup',
              psfs: this.psfs,
            },
            wrapped_input_id ? deleteJobInputItem : deleteJobInputGroupItem,
          )
        },
      ),
    )
    actions.add(
      new Action(
        'arrow_forward',
        formatMessage(messages.goToInputs),
        row => {
          const {
            wrapped_input_group_id,
            title,
            workflow_task_title,
            workflow_title,
          } = row
          const params = new URLSearchParams()

          params.append('inputGroupId', wrapped_input_group_id)
          params.append('inputGroupTitle', title)
          params.append('taskTitle', workflow_task_title)
          params.append('jobTitle', workflow_title)

          if (search && search.length) {
            params.append('backUrlParams', search)
          }

          params.append('inputGroup', wrapped_input_group_id)

          history.push({
            pathname: ROUTE.JOB_WORKFLOW_INPUTS,
            search: params.toString(),
          })
        },
        {
          callback: ({ wrapped_input_group_id }) => ({
            hidden: !wrapped_input_group_id,
          }),
        },
      ),
    )

    return actions
  }

  getInputsTableData() {
    const {
      fetchJobInputItemData,
      location: { search },
    } = this.props

    if (search && search.length) {
      const params = new URLSearchParams(search)

      this.psfs.addFilter('task', params.get('taskId'))
    }

    fetchJobInputItemData({ psfs: this.psfs })
  }

  handleChangeDense() {
    this.setState(prevState => ({
      isDenseRows: !prevState.isDenseRows,
    }))
  }
}

JobInputItemList.propTypes = {
  tableData: PropTypes.object.isRequired,
  loader: PropTypes.bool.isRequired,
  fetchJobInputItemData: PropTypes.func,
  deleteJobInputItem: PropTypes.func,
  deleteJobInputGroupItem: PropTypes.func,
  jobInputsClearStore: PropTypes.func,
}

JobInputItemList.defaultProps = {
  fetchJobInputItemData: () => {},
  deleteJobInputItem: () => {},
  deleteJobInputGroupItem: () => {},
  jobInputsClearStore: () => {},
}

export default connect(
  state => ({
    tableData: state.getIn(['jobInputItemsAdmin', 'tableData']),
    loader: state.getIn(['jobInputItemsAdmin', 'isLoadingTableData']),
    licensee: state.getIn(['profile', 'profile', 'licensee']),
  }),
  {
    fetchJobInputItemData,
    deleteJobInputItem,
    deleteJobInputGroupItem,
    jobInputsClearStore,
    fetchJobInputPositionUpdate,
  },
)(injectIntl(JobInputItemList))
